import React, { useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem } from '@mui/material';
import { ApiService, RetrieveDocumentGenerationJob } from 'api';
import { useResponse } from 'services/hooks';
import DocumentGenerationJobTableItemCapTableMenuProps from './DocumentGenerationJobTableItemCapTableMenu.props';
import { useStore } from '../../../../../../common/store';
import { UseStoreValues } from '../../../../../../common/types/store';
import { Logger } from '../../../../../../utilities';
import { AIGradientButton, AIGradientSpinner, AIGradientText } from '../../../AIGradient';

const DocumentGenerationJobTableItemCapTableMenu = ({
  generationJob,
  onClose,
}: DocumentGenerationJobTableItemCapTableMenuProps) => {
  const [loading, setLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const { successNotification, errorNotification } = useResponse();
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { companyInfo } = storeValue;

  const hasMetadata =
    generationJob.metadata &&
    generationJob.metadata.measurement_date_id;

  const handleApplyClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleApplyConfirm = async () => {
    setConfirmDialogOpen(false);
    setLoading(true);

    try {
      if (!companyInfo?.id) {
        errorNotification('Company information not available');
        return;
      }

      Logger.info(`Applying cap table generation job ${generationJob.job_id}`);

      // Cast: the generated response type has no already_applied yet (codegen drift).
      const result = (await ApiService.apiCompanyCapTablesApplyGenerationJobCreate(companyInfo.id.toString(), {
        job_id: generationJob.job_id,
      })) as unknown as { already_applied?: boolean; security_ids: number[] };

      // already_applied = the server's exactly-once guard no-opped (another
      // tab or the app-load resume won); tell the user honestly.
      successNotification(
        result.already_applied
          ? 'These AI results were already applied to the cap table'
          : `Cap table updated with ${result.security_ids?.length} securities from AI results`
      );

      if (onClose) {
        onClose();
      }
    } catch (err) {
      Logger.error('Error applying cap table generation job:', err);
      const apiError = err as { body?: { error?: string }; message?: string } | undefined;
      const errorMessage = apiError?.body?.error || apiError?.message || 'Failed to apply cap table results';
      errorNotification(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmCancel = () => {
    setConfirmDialogOpen(false);
  };

  const isCompleted = generationJob.status === RetrieveDocumentGenerationJob.status.COMPLETED;

  return (
    <>
      <MenuItem onClick={handleApplyClick} disabled={loading || !isCompleted}>
        <Box display="flex" alignItems="center" gap={1}>
          {loading && <AIGradientSpinner />}
          <AIGradientText>{loading ? 'Applying...' : 'Apply to Cap Table'}</AIGradientText>
        </Box>
      </MenuItem>

      <Dialog open={confirmDialogOpen} onClose={handleConfirmCancel}>
        <DialogTitle>Apply AI Results to Cap Table</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will apply the AI-extracted securities to the cap table. Results that were
            already applied are detected and will not be applied a second time.
            {!hasMetadata && (
              <>
                <br /><br />
                <strong>Note:</strong> No measurement date metadata was found on this job.
                The results will be applied to the most recent measurement date for this company.
              </>
            )}
            <br /><br />
            Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmCancel} color="inherit">
            Cancel
          </Button>
          <AIGradientButton onClick={handleApplyConfirm}>Apply</AIGradientButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentGenerationJobTableItemCapTableMenu;
